// analytics.ts — FE-only telemetry dispatcher for aha_forge events.
//
// Spec: docs/contracts/aha/telemetry_events.aha.v1.yaml (11 events).
// Transports: window.gtag (Google Analytics) + window.posthog?.capture.
// Fail-silent: never throws to caller. PII guards enforced upstream — caller
// must never pass email, wallet_address, or raw topic_text. We strip those
// keys defensively here.

const PII_KEYS = new Set([
  'email',
  'full_email',
  'wallet_address',
  'raw_topic_text',
  'topic_text',
  'chat_content',
  'name',
]);

const DEV = (() => {
  try {
    return import.meta.env.DEV === true;
  } catch {
    return false;
  }
})();

declare global {
  interface Window {
    gtag?: (cmd: string, eventName: string, props: Record<string, unknown>) => void;
    posthog?: { capture: (eventName: string, props: Record<string, unknown>) => void };
    __DEMO_AUTO_USER?: boolean;
  }
}

export function isDemoUser(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.__DEMO_AUTO_USER === true) return true;
  try {
    const env = import.meta.env as unknown as Record<string, string | undefined>;
    return env.VITE_DEMO_AUTO_USER === '1';
  } catch {
    return false;
  }
}

export function emailLocal(email: string): string {
  const at = email.indexOf('@');
  return at === -1 ? email : email.slice(0, at);
}

function scrub(props: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (PII_KEYS.has(k)) continue;
    out[k] = v;
  }
  return out;
}

export function fireAhaEvent(eventName: string, props: Record<string, unknown>): void {
  if (isDemoUser()) {
    if (DEV) {
      // eslint-disable-next-line no-console
      console.log(`[aha-telemetry · demo · suppressed] ${eventName}`, props);
    }
    return;
  }
  const safe = scrub(props);
  if (DEV) {
    // eslint-disable-next-line no-console
    console.log(`[aha-telemetry] ${eventName}`, safe);
  }
  try {
    if (typeof window !== 'undefined') {
      window.gtag?.('event', eventName, safe);
      window.posthog?.capture(eventName, safe);
    }
  } catch {
    // fail silent — telemetry must never break the UX
  }
}
